// ht-tool中的,所有原生交互方法

import { objToQuerystring, getUrlKey, canIuseVersion } from "../helpers";
export {
  nativeWeChatPay,
  nativeAliPaytPay,
  nativeBraintreePay,
  nativeCheckoutPay
} from "./pay";
import { isIOSPhone, isAndroidPhone } from "../is";
import {
  SelectFriendParams,
  OpenPhotoPopupParams,
  type OpenPhotoPopupResult
} from "../interface";
import { normalizeOpenPhotoPopupResult, openPhotoPopupByInput } from "./helper";

import { DEEPLINK } from "./enum-deeplink";
import {
  CalendInter,
  UserInfo,
  GotoLive,
  EditPlanLive,
  ClassInter,
  ChatShareInfoInter,
  ShareInfoInter,
  LogInter,
  LearnCardShareInter,
  ShareCardInter,
  translateText,
  SocialShareInter,
  ImgShareChatInter,
  IGetImageWordDataResponse,
  type platCtrlType
} from "../interface";

/**
 * @description 返回上一页
 * @returns {void}
 * @example import { goBack } from "@ht/utils";
 * goBack();
 */
export const goBack = () => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.goBack.postMessage({});
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(DEEPLINK.GO_BACK);
  }
};

/**
 * @description 跳转链接, 跳转App Store,可以跳转deeplink
 * @param {string} 跳转链接
 * @example import { gotoHTDeepLink } from "@ht/utils";
 * gotoHTDeepLink(`${DEEPLINK.MOMENT_PAGE}?tag=${tag}`);
 */
export const gotoHTDeepLink = async (
  url: string,
  isCloseWebview: boolean = false
) => {
  if (isIOSPhone) {
    await window.webkit.messageHandlers.onDeepLinkCallback.postMessage(
      `${encodeURI(url)}`
    );
  } else if (isAndroidPhone) {
    await window.hellotalk.onHtDeepLinkCallback(url);
  } else {
    console.log("非客户端");
  }
  if (isCloseWebview) {
    goBack();
  }
};

/**
 * @description 关闭webview
 * @param {boolean} reference - 是否多关闭一次，用于有H5之间跳转的情况，默认false
 * @example import { closeWebview } from "@ht/utils";
 * closeWebview();
 */
export const closeWebview = (referrer = false) => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.goBack.postMessage({});
    if (document.referrer && referrer) {
      window.webkit.messageHandlers.goBack.postMessage({});
    }
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(DEEPLINK.CLOSE_WEBVIEW);
  }
};

/**
 * @description 跳转个人页面profile
 * @param {string} userid - 用户id
 * @example import { anotherPerson } from "@ht/utils";
 * anotherPerson(userid);
 */
export const anotherPerson = (userid: string, source?: string) => {
  const URL = `hellotalk://profile?userid=${userid}&source=${source || ""}`;
  if (isIOSPhone) {
    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(URL);
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(URL);
  }
};

/**
 * @description 咨询、聊天
 * @param {number|string} jid- 用户jid或者公众号ID
 * @example import { chatSingle } from "@ht/utils";
 * chatSingle(jid);
 */
export const chatSingle = (
  jid: number | string,
  source: string,
  msgId?: string
) => {
  const URL = `${DEEPLINK.CHAT_SINGLE}/${jid}?source=${source}&msgId=${msgId}`;
  if (isIOSPhone) {
    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(URL);
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(URL);
  }
};

/**
 * @description 打印日志到APP
 * @param {LogInter} data - 日志信息
 * @example import { nativeLog } from "@ht/utils";
 * nativeLog({
 * })
 */
export const nativeLog = (data: LogInter) => {
  if (isIOSPhone) {
    try {
      //ios不需要转
      window.webkit.messageHandlers.logUtils.postMessage(data);
    } catch (error) {
      console.log("IOS 上传日志失败");
    }
  } else if (isAndroidPhone) {
    try {
      const json = JSON.stringify(data);
      window.hellotalk.logUtils(json);
    } catch (error) {
      console.log("Android 上传日志失败");
    }
  }
};

/**
 * @description 判断是不是HelloTalk内部
 * @returns {Boolean} 返回值true | false
 * @example import { isHelloTalk } from "@ht/utils";
 * isHelloTalk();
 */
export const isHelloTalk = (): boolean => {
  let flag = false;
  const userAgent = window.navigator.userAgent;
  if (isAndroidPhone) {
    return userAgent.indexOf("hellotalk") >= 0;
  } else {
    if (userAgent.indexOf("hellotalk") >= 0) {
      return true;
    }
    // IOS需要低版本兼容,判断能否调起方法
    try {
      window.webkit.messageHandlers.userInfo.postMessage({});
      flag = true;
    } catch (e) {
      flag = false;
    }
  }
  return flag;
};

/**
 * @description 写入日历
 * @param {CalendInter} calendInfo - 日历信息
 * @example import { addCalendar } from "@ht/utils";
 * addCalendar({
 *  title: 'HelloTalk',
 *  desc: "",
 *  startTime:"",
 *  endTime:""
 * })
 */
export const addCalendar = (calendInfo: CalendInter) => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.addCalendarEvent.postMessage(calendInfo);
  } else if (isAndroidPhone) {
    window.hellotalk.addCalendarEvent(JSON.stringify(calendInfo));
  }
};

/**
 * @description 保存图片到相册
 * @param {String} - url  图片url地址
 * @returns {Promise<string>} - 返回状态  1，保存成功。0 保存失败
 * @example import { saveImage } from "@ht/utils";
 * saveImage(url).then(res => console.log(res))
 */
export const saveImage = (url: string) => {
  // console.log("传进来的url", url);
  return new Promise(resolve => {
    window.saveImageCallBack = (status: string) => {
      resolve(status);
    };
    if (isIOSPhone) {
      window.webkit.messageHandlers.saveImage.postMessage({ url });
    } else if (isAndroidPhone) {
      window.hellotalk.saveImage(url);
    }
  });
};

/**
 * @description 打开系统相册弹窗，支持单选和多选
 * @param {OpenPhotoPopupParams} params - 相册选择参数
 * @returns {Promise<OpenPhotoPopupResult>}
 */
export const openPhotoPopup = (params: OpenPhotoPopupParams = {}) => {
  const { multiple = false } = params;

  return new Promise<OpenPhotoPopupResult>(async resolve => {
    const fallbackToInput = async () => {
      const result = await openPhotoPopupByInput(params);
      resolve(result);
    };

    if (!isIOSPhone) {
      await fallbackToInput();
      return;
    }

    try {
      window.openPhotoPopupCallback = (
        res: { data: string[] } | string[] | string | null | undefined
      ) => {
        window.openPhotoPopupCallback = null;
        resolve(normalizeOpenPhotoPopupResult(res, multiple));
      };
      await window.webkit.messageHandlers.openPhotoPopup.postMessage({
        multiple
      });
    } catch (error) {
      console.log("openPhotoPopup error", error);
      await fallbackToInput();
    }
  });
};

/**
 * @description 获取用户信息
 * @returns {Promise<UserInfo | undefined> } 返回用户信息
 */
export const getAppUserInfo = (): Promise<UserInfo | undefined> => {
  return new Promise(async (resolve, reject) => {
    if (isIOSPhone) {
      const onHellotalkOnUserInfoCallBack = (res: string) => {
        const data = JSON.parse(res);
        data.userId = data.userid; //ios返回的用户信息userId字段跟安卓不一致
        data.language = data.Language; //ios返回的用户信息language字段跟安卓不一致
        data.lang = data.appLang; //ios返回的界面语言跟安卓不一致
        resolve(data);
      };

      window.hellotalkOnUserInfoCallBack = onHellotalkOnUserInfoCallBack;
      try {
        await window.webkit.messageHandlers.userInfo.postMessage({});
      } catch (e) {
        reject(undefined);
        console.log("非IOS客户端", e);
      }
    } else if (isAndroidPhone) {
      if (window.hellotalk) {
        const data = await window.hellotalk.kolUserInfo();
        const res = JSON.parse(data);
        resolve(res);
      } else {
        reject(undefined);
        console.log("非Android客户端");
      }
    } else {
      reject(undefined);
      console.log("其它客户端");
    }
  });
};

/**
 * @description 网络状态反馈
 * @returns {Boolean} true有网络，false无网络
 */
export const getNetworkStatus = (): Promise<boolean> => {
  return new Promise(resolve => {
    if (isIOSPhone) {
      window.webkit.messageHandlers.getNetworkStatus.postMessage({});
      window.hellotalkGetNetworkStatusCallBack = (
        data: "wifi" | "4g" | "3g" | "2g" | "wwan" | "" | null | "null"
      ) => {
        if (!data || data === "null") {
          resolve(false);
        } else {
          resolve(true);
        }
      };
    } else if (isAndroidPhone) {
      resolve(window.hellotalk.getNetworkStatus());
    }
  });
};

/**
 * @description 提供静音以及音量反馈
 * @returns {Number}
 * @example 安卓1-100 IOS 0-1
 */
export const getVoiceStatus = (): Promise<number> => {
  return new Promise(resolve => {
    if (isIOSPhone) {
      window.webkit.messageHandlers.getVoiceStatus.postMessage({});
      window.hellotalkGetVoiceStatusCallBack = (data: number) => {
        resolve(data * 100);
      };
    } else if (isAndroidPhone) {
      resolve(window.hellotalk.getVoiceStatus());
    }
  });
};

/**
 * @description 跳转直播间-旧直播zego
 * @returns {void}
 */
export const gotoLive = (params: GotoLive): void => {
  const url = `${DEEPLINK.GOTO_LIVE}?${objToQuerystring(params)}`;
  if (isIOSPhone) {
    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(url);
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(url);
  }
};

/**
 * @description 编辑计划直播-旧直播zego
 * @returns  {void}
 */
export const editPlanLive = (params: EditPlanLive): void => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(params);
  } else if (isAndroidPhone) {
    const url = `${DEEPLINK.EDIT_LIVE}?${objToQuerystring(params)}`;
    window.hellotalk.onDeepLinkCallback(url);
  }
};

/**
 * @description 设置顶部状态栏颜色
 * @param {String} - 设置的颜色 哈希值
 * @example setNavbarBackground('#d15037')
 */
export const setNavbarBackground = (color: string) => {
  if (isIOSPhone) {
    window.hellotalkSetNavbarBackgroundCallBack =
      function onHellotalkSetNavbarBackgroundCallBack() {};
    const iosRes = {
      hexcolor: color
    };
    window.webkit.messageHandlers.setNavbarBackground.postMessage(iosRes);
  } else if (isAndroidPhone) {
    try {
      window.hellotalk.setNavbarBackground(color);
    } catch (error) {
      console.error(error);
    }
  }
};

/**
 * @description 视频播放
 * @param {Array} val - 视频播放参数
 * @example videoPlay(['https://ht-blog.oss-cn-hongkong.aliyuncs.com/teach/record/aced3309074206876c94499861ed8041_FC-10284963-1658993212669_0.mp4'])
 */
export const videoPlay = (val: string[]) => {
  const urls = `hellotalk://play_video?data={"urls":${JSON.stringify(val)}}`;
  if (isIOSPhone) {
    console.log(66);

    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(urls);
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(urls);
  }
};

/**
 * @description 加载声网教室
 * @param {ClassInter} val - 进入教室参数
 *
 */
export const endAgoClass = (val: ClassInter) => {
  const objStr = objToQuerystring(val);
  const url = `hellotalk://gotoClass?${objStr}`;
  if (isIOSPhone) {
    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(url);
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(url);
  }
};

/**
 * @description HelloTalk 分享卡片至聊天界面,语伴分享卡片到聊天，链接可以点击
 * @param {Object} ChatShareInfoInter - 分享信息

 */
export const helloTalkShare = (shareInfo: ChatShareInfoInter) => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.helloTalkShare.postMessage(shareInfo);
  } else if (isAndroidPhone) {
    const info = JSON.stringify(shareInfo);
    const URL = `hellotalk://shareCard/chat?info=${info}`;
    window.hellotalk.onDeepLinkCallback(URL);
  }
};

/**
 * @description HelloTalk 仅分享图片至聊天界面，无链接，点击图片不会跳转
 * version 5.2.12
 * @param {Object} ImgShareChatInter - 分享信息
 */
export const helloTalkShareImgInChat = (shareInfo: ImgShareChatInter) => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.helloTalkShare.postMessage(shareInfo);
  } else if (isAndroidPhone) {
    const info = JSON.stringify(shareInfo);
    const URL = `hellotalk://share/chat?info=${info}`;
    window.hellotalk.onDeepLinkCallback(URL);
  }
};

/**
 * @description HelloTalk动态分享,分享到moment
 * @param {Object} ShareInfoInter - 分享信息
 */
export const dynamicShare = (shareInfo: ShareInfoInter) => {
  console.log("执行到这");

  if (isIOSPhone) {
    const info = {
      data: JSON.stringify(shareInfo)
    };
    window.webkit.messageHandlers.dynamicShare.postMessage(info);
  } else if (isAndroidPhone) {
    const info = JSON.stringify(shareInfo);
    const URL = `hellotalk://share/moment?info=${info}`;
    window.hellotalk.onDeepLinkCallback(URL);
  }
};

/**
 * @description HelloTalk动态分享-比dynamicShare方法的简单版本，仅用于唤起动态分享界面，仅携带一个话题，内容为空的可以用这个方法
 * @param {String} tag - 话题id

 */
export const easyDynamicShare = (tag?: string) => {
  gotoHTDeepLink(`${DEEPLINK.MOMENT_PAGE}?tag=${tag}`);
};

/**
 * @description HelloTalkd动态卡片分享
 * @param {Object} ShareInfoInter - 分享信息
 * @example dynamicCardShare({
    content: '',
    learn_card: {
      goto_url: `https://qtest.hellotalk8.com/hybrid_h5/language/LanguagePartner?htdisableselectedword=1&&hidebar=1&hidebottombar=1`,
      pic_url:
        'https://ali-hk-cdn.hellotalk8.com/ht-global/liveclass/1/221207/3/10284963/s/0/f64d6ddb3fc8c56180832a06c847d933.png',
      width: 315,
      height: 455,
    },
  })
 */
export const dynamicCardShare = (shareInfo: ShareCardInter) => {
  if (isIOSPhone) {
    const info = {
      data: JSON.stringify(shareInfo)
    };
    window.webkit.messageHandlers.dynamicShare.postMessage(info);
  } else if (isAndroidPhone) {
    const info = JSON.stringify(shareInfo);
    const URL = `hellotalk://share/moment?info=${info}`;
    window.hellotalk.onDeepLinkCallback(URL);
  }
};

/**
 * @description 分享卡片到聊天 大图
 * @param {Object} shareInfo - 分享信息
 * @example learnCardShare({
        goto_url: 'https://qtest.hellotalk8.com/liveclass/home/',
        pic_url: 'https://ali-hk-cdn.hellotalk8.com/ht-global/liveclass/1/221207/3/10284963/s/0/f64d6ddb3fc8c56180832a06c847d933.png',
        width: 315,
        height: 455,
    });
 */
export const learnCardShare = (shareInfo: LearnCardShareInter) => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.learnCardShare.postMessage(shareInfo);
  } else if (isAndroidPhone) {
    const info = JSON.stringify(shareInfo);
    window.hellotalk.learnCardShare(info);
  }
};
/**
 * @description 语伴跳转查看录音
 * @param {Object} id - 课程id
 * @example viewRecord(123456)
 */
export const exchangeLanguageRecord = (id: number) => {
  const URL = `hellotalk://profile/exchange_language_record?id=${id}`;
  if (isIOSPhone) {
    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(URL);
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(URL);
  }
};

/**
 * @description 获取翻译后的内容,H5文本翻译，传入需要翻译的文本和语种列表，返回翻译后的文本
 * version 5.1.0
 * @param {translateText}
 * @example
 * --@id id {string} 时间戳字符串
 * --@text text {string} 需要翻译的内容--需处理换行符
 * --@langs langs {string[]} 需要翻译成的语种缩写列表
 * --@service_scenario {string} 翻译数据上报
   --传参示例：
   const params = {
    "id": new Date().getTime().toString(), // 时间戳字符串，表示翻译标识id
    "text": "Whats up man",
    "langs": ["zh-CN", "en"],
    "service_scenario": "Host Introduction(H5)"
   }
 * return json
 * --id {string} 时间戳 与传入参数id一致
 * --translates {string[]} 翻译后的内容，分别对应传入的语种列表
   -- 返回数据示例：
   {
    "id": "1234567890123", // 翻译标识id
    "translates": ["怎么样", "Whats up man"] // 一一对应要翻译的语种内容
   }
 *
 * 对应语种：
    "zh-CN": "Chinese",
    "tc": "Chinese_yy", // 粤语
    "en": "English",
    "ja": "Japanese",
    "ko": "Korean",
    "es": "Spanish",
    "fr": "French",
    "de": "German",
    "pt": "Portuguese",
    "it": "Italian",
    "ru": "Russian",
    "tr": "Turkish",
    "ar": "Arabic",
*/

const getTranslateTextQueue: Array<(value: unknown) => void> = [];
export const getTranslateText = (params: translateText) => {
  return new Promise(async (resolve, reject) => {
    let txt = params.text.replace(/\n/g, ".");
    if (["Chinese", "Chinese_yy"].includes(params.localLang)) {
      txt = params.text.replace(/\n/g, "。");
    }
    params.text = txt;
    getTranslateTextQueue.push(resolve);
    const onTranslateCall = (res: string) => {
      const data = JSON.parse(decodeURIComponent(res));
      getTranslateTextQueue.pop()?.(data);
    };
    window.translateCall = onTranslateCall;
    if (isIOSPhone) {
      await window.webkit.messageHandlers.translateText.postMessage(params);
    } else if (isAndroidPhone) {
      if (window.hellotalk) {
        try {
          await window.hellotalk.translateText(JSON.stringify(params));
        } catch (e) {
          reject({ msg: "error" });
        }
      } else {
        reject({ msg: "error" });
      }
    }
  });
};

/**
 * @description 获取wnsconfig域名,域名信息，CDN等配置
 */

export const getWnsConfig = () => {
  return new Promise(resolve => {
    function hellotalkWnsConfigCallBack(data) {
      resolve(data);
    }
    window.hellotalkWnsConfigCallBack = hellotalkWnsConfigCallBack;
    if (isIOSPhone) {
      window.webkit.messageHandlers.getWnsConfig.postMessage("wns");
    } else if (isAndroidPhone) {
      window.hellotalk.getWnsConfig();
    }
  });
};

/**
 * @description 获取商品信息
 */
export const getProductInfoFromStore = async params => {
  try {
    if (isIOSPhone) {
      const result = await new Promise(resolve => {
        const getProductInfoFromStoreCallBack = (response: string) => {
          resolve(response);
        };
        window.getProductInfoFromStoreCallBack =
          getProductInfoFromStoreCallBack;
        window.webkit.messageHandlers.getProductInfoFromStore.postMessage(
          params
        );
      }).catch((error: Error) => {
        console.log(error, "error");
        return {};
      });
      return result;
    } else if (isAndroidPhone) {
      const data = window.hellotalk.getProductInfoFromStore(params);
      return data;
    }
  } catch {
    console.log("获取商品信息 error");
  }
};

/**
 * @description 隐藏分享到外部平台
 * @param {Object} shareInfo - 分享信息
 * @example socialShare({ plat_ctrl: hideOffsitePlatformParam })
 */
export const hideOffsitePlatformParam = ["cn", "kr", "jp", "other"].reduce(
  (acc, cur) => {
    const platform = [
      "weixin_chat",
      "weixin_moment",
      "weibo",
      "fb",
      "qq",
      "twitter",
      "instagram",
      "line",
      "whatsapp",
      "kakao"
    ];
    acc[cur] = platform.map(item => ({
      platform: item,
      status: 1
    }));
    return acc;
  },
  {}
) as platCtrlType;

/**
 * @description 原生分享
 * @param {Object} ShareInfoInter - 分享信息
 */
export const socialShare = (
  shareInfo: SocialShareInter,
  callback?: () => void
) => {
  const onSocialShareCallback = callback ?? function noop() {};
  if (isIOSPhone) {
    const info = {
      data: JSON.stringify(shareInfo)
    };
    window.socialShareCallback = onSocialShareCallback;
    window.webkit.messageHandlers.socialShare.postMessage(info);
  } else if (isAndroidPhone) {
    const info = JSON.stringify(shareInfo);
    window.socialShareCallback = onSocialShareCallback;
    window.hellotalk.socialShare(info);
  }
};

/**
 * @description 唤起贴文推广付费页面
 * @param {String} mid 帖文id字符串
 * @param {String} source 来源字符串（用于埋点）
 */
export const openMomentExposure = (params: {
  mid: string;
  source: string;
}): void => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.openMomentExposure.postMessage(params);
  } else if (isAndroidPhone) {
    window.hellotalk.openMomentExposure(JSON.stringify(params));
  }
};

/**
 * @description 生成二维码url。传入一个url，客户端返回一个二维码，会做这些逻辑：如果安装HT就打开，没安装跳转到商店，安装没登录，跳转到登录流程
 * version 5.2.30
 * @params {string} params.channel 场景参数 值:creatorreport
 * @params {ShareUrl} params.url URL
 * @returns {string} url 二维码url
 * @example 海外链接: https://hellotalk.allapp.link/8ivNDFJY74TbNXxwbnFMWV
 * 海内链接: https://hellotalk.allapp.link/3aYjcBNsCf7bjR7jZMmBep
 */
export type ShareUrl =
  | "https://hellotalk.allapp.link/8ivNDFJY74TbNXxwbnFMWV"
  | "https://hellotalk.allapp.link/3aYjcBNsCf7bjR7jZMmBep";

export const generateShareUrl = (params: {
  channel: "creatorreport";
  url: ShareUrl;
}) => {
  return new Promise<string>(async (resolve, reject) => {
    if (isIOSPhone) {
      const onGenerateShareCode = (res: string) => {
        console.log("onGenerateShareCode IOS ===> res", res);
        resolve(res);
      };
      window.generateShareCode = onGenerateShareCode;
      try {
        await window.webkit.messageHandlers.generateShareUrl.postMessage(
          params
        );
      } catch (error) {
        console.log("generateShareUrl Error:非客户端", error);
        reject(params.url);
      }
    } else if (isAndroidPhone) {
      const onGenerateShareCode = (res: string) => {
        console.log("onGenerateShareCode Android ===> res", res);
        resolve(res);
      };
      window.generateShareCode = onGenerateShareCode;
      try {
        await window.hellotalk.generateShareUrl(JSON.stringify(params));
      } catch (error) {
        console.log("generateShareUrl Error:非客户端", error);
        reject(params.url);
      }
    }
  });
};

/**
 *
 * @description 直接登录HelloTalk账户
 * version 5.4.10
 * @params {Object} login_type 登录平台
 * @params {Object} bind_id 直接登录校验码（服务端返回）
 * @params {Object} business_type 登录类型,登陆后的落地页，0：普通首页，1：用户账户页面
 * @example login_type枚举值: 1-email,4=facebook,5=wechat,6=qq,7=google,8=apple,9=一键登录,10=手机号,11=line,12=kakao
 */
export const bindLogin = (params: {
  login_type: number;
  bind_id: string;
  business_type: number;
}) => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.bindLogin.postMessage(JSON.stringify(params));
  } else if (isAndroidPhone) {
    window.hellotalk.bindLogin(JSON.stringify(params));
  }
};

/**
 * @description 打开手机国际区号选择页
 * version 5.4.10
 */
export const openSelectPhoneCodePage = () => {
  return new Promise(async resolve => {
    const onSelectPhoneCodePageCallback = (res: string) => {
      const data = JSON.parse(decodeURIComponent(res));
      resolve(data);
    };
    window.selectPhoneCodePageCallback = onSelectPhoneCodePageCallback;
    try {
      if (isIOSPhone) {
        await window.webkit.messageHandlers.openSelectPhoneCodePage.postMessage(
          {}
        );
      } else if (isAndroidPhone) {
        await window.hellotalk.openSelectPhoneCodePage();
      }
    } catch (e) {
      console.log("打开手机区号选择页error", e);
    }
  });
};

/**
 * @description 打开语言界面选择页,App中的设置-翻译目标语言和这个列表一样
 * version 5.4.10
 */
export const openSelectLanguagePage = () => {
  return new Promise(async resolve => {
    const onSelectLanguagePageCallback = (res: string) => {
      const data = JSON.parse(decodeURIComponent(res));
      resolve(data);
    };
    window.selectLanguagePageCallback = onSelectLanguagePageCallback;
    try {
      if (isIOSPhone) {
        await window.webkit.messageHandlers.openSelectLanguagePage.postMessage(
          {}
        );
      } else if (isAndroidPhone) {
        await window.hellotalk.openSelectLanguagePage();
      }
    } catch {
      console.log("打开语言界面选择页error");
    }
  });
};

/**
 *
 * @description 跳转第三方登录界面
 * version 5.4.60
 */
export const openThirdLogin = (params: Record<string, unknown>) => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.openThirdLogin.postMessage(
      JSON.stringify(params)
    );
  } else if (isAndroidPhone) {
    window.hellotalk.openThirdLogin(JSON.stringify(params));
  }
};

/**
 * @description 通用方法获取登录前的服务开关,number需要找客户端对一下确定是哪一个
 * version 5.4.60
 * @param {Object}
 * @example  number:1表示决定展示“忘记密码”页面新老UI的
 */
export const fetchClientData = (params: { type: number }) => {
  return new Promise(async resolve => {
    const onClientResponseCallback = (response: string) => {
      const data = JSON.parse(response);
      resolve(data);
    };
    window.onClientResponse = onClientResponseCallback;
    try {
      if (isIOSPhone) {
        await window.webkit.messageHandlers.fetchClientData.postMessage(
          JSON.stringify(params)
        );
      } else if (isAndroidPhone) {
        await window.hellotalk.fetchClientData(JSON.stringify(params));
      }
    } catch (e) {
      console.log("获取登录前的服务开关error", e);
    }
  });
};

/**
 * @description  跳转应用权限设置页面
 * version 5.2.40
 */
export const openPushNotifySetting = () => {
  try {
    if (isIOSPhone) {
      window.webkit.messageHandlers.openPushNocifySetting?.postMessage({});
    } else if (isAndroidPhone) {
      if (window.hellotalk) {
        window.hellotalk.openPushNotifySetting &&
          window.hellotalk.openPushNotifySetting();
      }
    }
  } catch (error) {
    console.log(error);
  }
};

/**
 * @description 获取设备顶部状态栏的高度，全屏有HS参数需要获取状态栏高度，不然navbar会顶到最上方，每个设备的状态栏高度不一样，全屏幕状态下，navbar顶部距离就是这个状态栏高度
 * version 5.4.70
 */
export const getStatusBarHeight = async (): Promise<number> => {
  if (getUrlKey(location.href, "HS") !== "1") return 0;
  try {
    if (isIOSPhone) {
      const result = await new Promise(resolve => {
        const getStatusBarHeightCallback = (response: string) => {
          resolve(parseInt(response));
        };
        window.getStatusBarHeightCallback = getStatusBarHeightCallback;
        window.webkit.messageHandlers.getStatusBarHeight.postMessage({});
      }).catch((error: Error) => {
        console.log(error, "error");
        return 0;
      });
      return result as number;
    } else if (isAndroidPhone) {
      const data =
        (JSON.parse(window.hellotalk.getStatusBarHeight()) as string) || "0";
      return parseInt(data);
    }
  } catch {
    console.log("get status bar height error");
  }
  return 0;
};

/**
 * @description 获取登录后的获取服务开关
 * version 5.5.20
 * @param {string} switchKey 开关所属活动名称 eg: "vip_validity_module"   vip福利页
 * iOS是异步的，需要写await await getSwitchConfig("xxxx")
 * @returns {Promise<JSON>}
 */
export const getSwitchConfig = (switchKey: string) => {
  return new Promise((resolve, reject) => {
    try {
      if (isIOSPhone) {
        const getSwitchConfigCallback = (res: string) => {
          try {
            resolve(JSON.parse(res));
          } catch (error) {
            reject(error);
          }
        };
        window.getSwitchConfigCallback = getSwitchConfigCallback;
        window.webkit.messageHandlers.getSwitchConfig.postMessage(switchKey);
      } else if (isAndroidPhone) {
        try {
          const data = window.hellotalk.getSwitchConfig(switchKey);
          resolve(JSON.parse(data));
        } catch (error) {
          reject(error);
        }
      } else {
        reject();
        console.log("非客户端");
      }
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * @description 用户是否打开通知权限,获取系统推送状态方法
 * version 5.2.40
 * @param {string} title 标题
 * @param {number | string} status 状态 0 -- 开启; 1 -- 未开启;
 * @returns {Promise<JSON>}
 */
export const getPushNotifyConfig = () => {
  return new Promise(async (resolve, reject) => {
    if (isIOSPhone) {
      const onPushNotifyConfigCallBack = res => {
        resolve(JSON.parse(res));
      };
      window.pushNotifyConfigCallBack = onPushNotifyConfigCallBack;
      try {
        await window.webkit.messageHandlers.getPushNotifyConfig.postMessage({});
      } catch (e) {
        reject();
        console.log("非客户端", e);
      }
    } else if (isAndroidPhone) {
      if (window.hellotalk) {
        try {
          const data = await window.hellotalk.getPushNotifyConfig();
          const result = JSON.parse(data);
          resolve(result);
        } catch (e) {
          reject();
          console.log("version < 5.2.40 获取权限通知配置失败");
        }
      } else {
        reject();
        console.log("非客户端");
      }
    }
  });
};

/**
 * @description 跳转直播间方法兼容新旧
 * version 5.5.0
 * @param {String} room_id  直播间房间id
 * @param {String} source 跳转来源，数据上报用
 * @param {String}  private_room_key 私密房key，有值代表为私密房，跳转时客户端判断能否进入
 *
 */
export const enterLive = (
  room_id: string,
  source: string,
  private_room_key?: unknown
) => {
  // 根据room_id区分新旧版本
  const regex = /^\d{19}$/;
  const testString = room_id.includes("_") ? room_id.split("_").pop() : room_id;
  let deeplink = "";
  // 例如新版本房间id格式固定为 xxx_xxx_00000000000000000 末尾一定是纯数字，所以正则验证包含下划线
  // 且末尾是纯数字的为新版本房间
  if (regex.test(testString as string)) {
    // 新版本
    deeplink = `${DEEPLINK.OPEN_LIVE_ROOM}?cname=${room_id}&private_room_key=${private_room_key}&source=${source}`;
  } else {
    deeplink = `${DEEPLINK.GOTO_LIVE}?roomid=${room_id}&private_room_key=${private_room_key}&source=${source}`;
  }
  gotoHTDeepLink(deeplink);
};

/**
 * @description 跳转语聊房方法兼容新旧
 * version 5.5.0
 * @param {String} room_id  语聊房房间id
 * @param {String} source 跳转来源，数据上报用
 * @param {String}  private_room_key 私密房key，有值代表为私密房，跳转时客户端判断能否进入
 *
 */
export const enterVoice = (
  room_id: string,
  source: string,
  private_room_key?: unknown
) => {
  // 根据room_id区分新旧版本
  const regex = /^\d{19}$/;
  const testString = room_id.includes("_") ? room_id.split("_").pop() : room_id;
  let deeplink = "";
  // 例如新版本房间id格式固定为 xxx_xxx_00000000000000000 末尾一定是纯数字，所以正则验证包含下划线
  // 且末尾是纯数字的为新版本房间
  if (regex.test(testString as string)) {
    deeplink = `${DEEPLINK.OPEN_VOICE_ROOM_NEW}?cname=${room_id}&private_room_key=${private_room_key}&source=${source}`;
  } else {
    deeplink = `${DEEPLINK.OPEN_VOICE_ROOM}?room_id=${room_id}&private_room_key=${private_room_key}&source=${source}`;
  }
  gotoHTDeepLink(deeplink);
};

/**
 * @description 唤起购买coins充值弹窗
 * version 5.1.4
 * @param {String} source  购买来源,数据上报用
 * version 5.7.10
 * @param {number} rechargeGearValue  对应的充值档位
 */
export const invokeRechargeCoins = (
  source?: string, // source值（数据上报)
  rechargeGearValue?: number, // 充值档位
  extVirtualValue?: number, //赠送金额
  isPopFirstCharge?: number | boolean //是否调起首充弹窗 0-不调起（默认值） 1-调起
) => {
  gotoHTDeepLink(
    `${DEEPLINK.CURRENCY_RECHARGE}?source=${source}&virtual_val=${rechargeGearValue}&ext_virtual_val=${extVirtualValue || 0}&is_pop_first_charge=${isPopFirstCharge || 0}`
  );
};

/**
 * @description 跳转蓝色特权页面
 * version unknow
 * @param {String} source  购买来源,数据上报用
 */
export const invokeVIPPageBlue = (source: string) => {
  gotoHTDeepLink(`${DEEPLINK.PURCHASE_NEW}?source=${source}`);
};

/**
 * @description 跳转恢复订阅页面,iOS自动打开App store的恢复订阅页面，Android跳转到google恢复订阅页面
 * version unknow
 */
export const recoverSubscriptions = () => {
  if (isIOSPhone) {
    gotoHTDeepLink(`${DEEPLINK.IOS_RECOVER_SUBSCRIPTION}`);
  } else {
    gotoHTDeepLink(`${DEEPLINK.ANDROID_RECOVER_SUBSCRIPTION}`);
  }
};

/**
 * @description 唤起购买vip页面
 * version unknow
 * @param {String} source  购买来源,数据上报用
 */
export const buyVIPSwiper = (
  url: string,
  source: string,
  trackSource: string
) => {
  /**
   * hellotalk://showVip?source=Custom Search Gender&trackSource=survey_h5
   * hellotalk://shop?source=Custom Search Gender&trackSource=survey_h5
   */
  /**
   * 各项特权触发：
    可通过链接下发：hellotalk://shop
    翻译次数:hellotalk://shop?source=Deeplink_Translation
    学/教多门语言:hellotalk://shop?source=Deeplink_Language
    额外曝光:hellotalk://shop?source=Deeplink_Extra_Exposure
    新语伴上限:hellotalk://shop?source=Deeplink_New_Chats
    解锁访客:hellotalk://shop?source=Deeplink_Unlock_Visitors
    无广告:hellotalk://shop?source=Deeplink_No_Ads
    附近的人:hellotalk://shop?source=Deeplink_Nearby
    无限语法改错:hellotalk://shop?source=Deeplink_Unlimited_Correction
   */
  gotoHTDeepLink(`${url}?source=${source}&trackSource=${trackSource}`);
};

/**
 * @description 发送表情打招呼（直接发送消息不唤起好友列表）
 * @param {Array} userId - 用户id
 * @param {Number} mode - 模式   mode = 0点击后跳转聊天界面  mode =1 直接发送，不跳转界面
 * @param {String} source - 来源
 * @returns {void}
 */
export const chatSayHi = (userId: number, mode: number, source?: string) => {
  const url = `hellotalk://chat_sayhi?userid=${userId}&mode=${mode}&source=${source}`;
  if (isIOSPhone) {
    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(url);
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(url);
  }
};

/***
 * 跳转主app
 */
export const gotoHellotalk = (url: string): void => {
  const userAgent = window.navigator.userAgent;
  const match = userAgent.match(/hellotalk\/([^\/]+)/);
  const version = match ? match[1] : null;
  if (isIOSPhone) {
    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(url);
  } else if (isAndroidPhone) {
    if (Number(version.replace(/\./g, "")) >= 500) {
      window.hellotalk.onHtDeepLinkCallback(url);
    } else {
      window.hellotalk.onDeepLinkCallback("wx" + url);
    }
  } else {
    console.log("非客户端");
  }
};

/**
 * 判断ios和android平台
 **/
type os = "Android" | "IOS" | "PC";
export const getOsType = (): os => {
  const device = navigator.userAgent;
  const isAndroid = device.includes("Android") || device.includes("Adr"); // 判断是否是 android终端
  const isIOS = !!device.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/); // 判断是否是 iOS终端
  return isAndroid ? "Android" : isIOS ? "IOS" : "PC";
};

/**
 * 跳转个人页面的动态tab
 * @param userid 用户id
 * @param source 来源
 */
export const anotherPersonDynamic = (userid: string, source?: string) => {
  const URL = `hellotalk://profile/moments_page?userid=${userid}&source=${source || ""}`;
  if (isIOSPhone) {
    window.webkit.messageHandlers.onDeepLinkCallback.postMessage(URL);
  } else if (isAndroidPhone) {
    window.hellotalk.onDeepLinkCallback(URL);
  }
};

/**
 * 调用客户端朗读文字功能 version: >= 5.6.0
 * @param {string} text - 需要朗读的文字
 * @param {string} source - 来源
 */
interface ICallSpeakTextParams {
  text: string;
  source: string;
  scene?: string;
}
export const callSpeakText = (params: ICallSpeakTextParams): void => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.speakText.postMessage(params);
  } else if (isAndroidPhone) {
    window.hellotalk.speakText(JSON.stringify(params));
  }
};

/**
 * 打开学习计划 -- 计划中的卡片
 * @param id 交换id
 * @param source 来源
 * @param cb 点击取消后的回调
 */
export const gotoExchangePlan = (
  id: string,
  source: string,
  cb: (res: string) => void // res: JSON字符串 { type: string; id: string }
) => {
  gotoHTDeepLink(`${DEEPLINK.LANG_EXCHANGE_PLAN}?id=${id}&source=${source}`);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalThis = window as any;
  if (
    !globalThis.exchangeCallBack ||
    typeof globalThis.exchangeCallBack !== "function"
  ) {
    globalThis.exchangeCallBack = cb;
  }
};

/**
 *  改变语言通知安卓刷新接口
 *  ver: 5.7.1
 */
export const notifyChangeLanguage = () => {
  if (isAndroidPhone) {
    window.hellotalk?.notifyLearnLangSelect?.();
  } else {
    console.log("is not Android");
  }
};

/**
 * 通知客户端领取体验卡成功
 */
interface ICallReceiveNoticeParams {
  feature_id: string;
  scene_id: string;
}
export const notifyCardClaimSuccess = (
  params: ICallReceiveNoticeParams
): void => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.notifyCardClaimSuccess.postMessage(params);
  } else if (isAndroidPhone) {
    window.hellotalk.notifyCardClaimSuccess(JSON.stringify(params));
  }
};

/**
 * 改变设备顶部导航栏字体颜色
 * @param color 颜色值 0 - 黑字 1 - 白字
 */
export const changeStatusBarTextColor = (color: 0 | 1): void => {
  try {
    if (isIOSPhone) {
      window.webkit.messageHandlers.setStatusBarColorMode.postMessage(
        `${color}`
      );
    } else if (isAndroidPhone) {
      window.hellotalk.setStatusBarColorMode(color);
    }
  } catch (error) {
    console.log("setStatusBarColorMode error: ", error);
  }
};

/*
 * 连胜打卡，告知回调函数--调出客户端的折扣卡弹窗
 */
export const winningStreakDiscountCard = (): void => {
  const timestamp = parseInt(String(new Date().getTime() / 1000)) + 86400;
  if (isIOSPhone) {
    window.webkit.messageHandlers.winningStreakDiscountCard.postMessage({
      timestamp
    });
  } else if (isAndroidPhone) {
    window.hellotalk.winningStreakDiscountCard(
      JSON.stringify({
        timestamp
      })
    );
  }
};

/**
 * @description 传给客户端当前该用户最新的佩戴图标
 * @param {String} url 佩戴图标url
 */
export const updateUserVipLogo = (url: string): void => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.updateUserVipLogo.postMessage(url);
  } else if (isAndroidPhone) {
    window.hellotalk.updateUserVipLogo(JSON.stringify(url));
  }
};

/**
 * @description 获取安卓蓝色特权页面中国安卓商品列表
 * @param period 商品周期 P1M、P3M、P1Y、Forever
 * @param scene 场景 "3"-蓝色特权页
 */
export const getProductInfoByPeriod = (
  period: string,
  scene: string
): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (isAndroidPhone) {
      const onProductInfoCallback = (res: string) => {
        resolve(res);
      };
      window.productInfoCallback = onProductInfoCallback;
      try {
        window.hellotalk.getProductInfoByPeriod(
          JSON.stringify({ period, scene })
        );
      } catch (error) {
        reject(error);
      }
    } else {
      reject(new Error("非安卓客户端"));
    }
  });
};

/**
 * @description 获取安卓商品信息
 * @param id: 商品id; scene: 场景; "3"-蓝色特权页
 */
export const getProductInfoWithPriceFromStore = (
  productId: string,
  scene: string
): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (isAndroidPhone) {
      const onProductGoogleInfo = (res: string) => {
        resolve(res);
      };
      window.productGoogleInfo = onProductGoogleInfo;
      try {
        window.hellotalk.getProductInfoWithPriceFromStore(
          JSON.stringify({ productId, scene })
        );
      } catch (error) {
        reject(error);
      }
    } else {
      reject(new Error("非安卓客户端"));
    }
  });
};

/**
 * @description 调用用户的选择语伴列表，选中后进行赠送VIP特权卡
 * version 5.4.10
 */
/**
 * @description 选择好友
 * @param {SelectFriendParams} params - 选择好友参数
 * @return {void}
 */
export const nativeSelectFriend = (params: SelectFriendParams) => {
  try {
    if (isIOSPhone) {
      const url = `${DEEPLINK.FRIENDS}?${objToQuerystring(params)}`;
      window.webkit.messageHandlers.onDeepLinkCallback.postMessage(url);
    } else if (isAndroidPhone) {
      const url = `${DEEPLINK.FRIENDS}?${objToQuerystring(params)}`;
      window.hellotalk.onDeepLinkCallback(url);
    }
  } catch (e) {
    console.log("选择好友error", e);
  }
};

/*
 * @description 删除账号后退出账号
 */
export const logOutAfterDeleteAccount = () => {
  if (isIOSPhone) {
    window.webkit.messageHandlers.logOutAfterDeleteAccount.postMessage({});
  } else if (isAndroidPhone) {
    window.hellotalk.logOutAfterDeleteAccount();
  }
};

/**
 * @description 获取客户端单词信息（跃问）
 * @version 6.0.80
 */
export const getImageWordData = () => {
  return new Promise<IGetImageWordDataResponse>((resolve, reject) => {
    try {
      if (isIOSPhone) {
        const getImageWordDataCallback = (res: IGetImageWordDataResponse) => {
          resolve(res);
        };
        (window as any).getImageWordDataCallback = getImageWordDataCallback;
        window.webkit.messageHandlers.getImageWordData.postMessage({});
      } else if (isAndroidPhone) {
        const res = window.hellotalk.getImageWordData();
        resolve(JSON.parse(res) as IGetImageWordDataResponse);
      }
    } catch (error) {
      if (error.name === "TypeError") {
        reject("version is too low");
      } else {
        reject(error);
      }
    }
  });
};

/**
 * @description 分享AI图片
 * @version 6.0.80
 */
export const shareAiPhoto = (res: IGetImageWordDataResponse) => {
  try {
    if (isIOSPhone) {
      window.webkit.messageHandlers.shareAiPhoto.postMessage(res);
    } else if (isAndroidPhone) {
      window.hellotalk.shareAiPhoto(JSON.stringify(res));
    }
  } catch (error) {
    console.log("shareAiPhoto error: ", error);
  }
};

/**
 * @description 获取客户端倒计时
 * @param {string} method 获取方法（默认为set） get:获取时间戳 set:触发新玩法并获取时间戳
 * @param {string} scene 场景（默认为”3“） 用于标识服务端的场景
 * @returns {object} vip商品信息：vip_promotion_end_time 结束时间戳（前端已处理为返回毫秒级）
 */
export const getVIPPromotionInfo = (
  method?: "get" | "set" | "",
  scene?: string
) => {
  console.log("params", method);
  return new Promise(async resolve => {
    const _type = getOsType();
    const onGetVIPPromotionInfoCallback = (res: string | object) => {
      let data = {};
      if (typeof res === "object") {
        // 如果返回类型为对象，深拷贝复用
        data = JSON.parse(JSON.stringify(res));
      } else if (typeof res === "string") {
        // 如果返回类型为字符串，解码并转为对象
        data = JSON.parse(decodeURIComponent(res));
      } else {
        // 如果返回类型不在对象和字符串的范围内，走默认值
        data = { vip_promotion_end_time: 0 };
      }
      const { vip_promotion_end_time } = data as {
        vip_promotion_end_time: number;
      };
      if (String(vip_promotion_end_time).length === 10) {
        // 如果返回的时间戳为秒级，统一转为毫秒级
        resolve({
          ...data,
          vip_promotion_end_time:
            (data as { vip_promotion_end_time: number })
              .vip_promotion_end_time * 1000
        });
      } else {
        resolve(data);
      }
    };
    window.getVIPPromotionInfoCallback = onGetVIPPromotionInfoCallback;
    try {
      // IOS 传参格式为对象，安卓传参格式为字符串
      if (_type === "IOS") {
        await window.webkit.messageHandlers.getVIPPromotionInfo.postMessage({
          method: method || "",
          scene: scene || ""
        });
      } else if (_type === "Android") {
        await window.hellotalk.getVIPPromotionInfo(
          JSON.stringify({ method: method || "", scene: scene || "" })
        );
      }
    } catch (e) {
      console.log("出错了", e);
      resolve({});
    }
  });
};

/**
 * @description 商城预览、赠送、购买、上报分类tab点击
 * @version 6.1.30
 */
export const handleH5MallAction = (params: {
  action: string;
  params: Record<string, unknown>;
  source: string;
}) => {
  if (isIOSPhone) {
    try {
      window.webkit.messageHandlers.handleH5MallAction.postMessage(
        JSON.stringify(params)
      );
    } catch (error) {
      console.log("handleH5MallAction-商城预览、赠送、购买-error: ", error);
    }
  } else if (isAndroidPhone) {
    try {
      window.hellotalk.handleH5MallAction(JSON.stringify(params));
    } catch (error) {
      console.log("handleH5MallAction-商城预览、赠送、购买-error: ", error);
    }
  }
};

/**
 * @description 首充礼包
 * @version 6.1.30
 */
export const handleFirstCharge = (params: {
  source: string;
  storeType?: string;
  entrace_way?: string;
}) => {
  if (isIOSPhone) {
    try {
      window.webkit.messageHandlers.handleFirstCharge.postMessage(
        JSON.stringify(params)
      );
    } catch (error) {
      console.log("handleFirstCharge-首充礼包-error: ", error);
    }
  } else if (isAndroidPhone) {
    try {
      window.hellotalk.handleFirstCharge(JSON.stringify(params));
    } catch (error) {
      console.log("handleFirstCharge-首充礼包-error: ", error);
    }
  }
};

/**
 * @description 调用原生激励广告预先加载
 * @param {string} sceneType 场景类型
 * @param {string} source 来源
 * @param {string} advertisingConfig 广告配置
 * @returns {object}
 */
export const callNativeAdvertisingPreload = (params: {
  sceneType: string;
  source?: string;
  advertisingConfig?: string;
}) => {
  return new Promise(async resolve => {
    const _type = getOsType();
    try {
      // IOS 传参格式为对象，安卓传参格式为字符串
      if (_type === "IOS") {
        console.log("IOS调用原生激励广告预先加载");
        await window.webkit.messageHandlers.callNativeAdvertisingPreload.postMessage(
          params
        );
      } else if (_type === "Android") {
        console.log("Android调用原生激励广告预先加载");
        await window.hellotalk.callNativeAdvertisingPreload(
          JSON.stringify(params)
        );
      }
    } catch (e) {
      console.log("出错了", e);
      resolve({});
    }
  });
};

/**
 * @description 调用原生激励广告
 * @param {string} sceneType 场景类型
 * @param {string} source 来源
 * @param {string} advertisingConfig 广告配置
 * @param {boolean} isPlayAdvertisement 是否播放广告
 * @returns {object}
 */
export const callNativeAdvertising = (params: {
  sceneType: string;
  source?: string;
  advertisingConfig?: string;
  isPlayAdvertisement?: boolean;
}) => {
  return new Promise(async resolve => {
    const _type = getOsType();
    const onCallNativeAdvertisingCallback = (res: string | object) => {
      let data = {};
      if (typeof res === "object") {
        // 如果返回类型为对象，深拷贝复用
        data = JSON.parse(JSON.stringify(res));
      } else if (typeof res === "string") {
        // 如果返回类型为字符串，解码并转为对象
        data = JSON.parse(decodeURIComponent(res));
      } else {
        // 如果返回类型不在对象和字符串的范围内，走默认值
        data = {};
      }
      resolve(data);
    };
    window.callNativeAdvertisingCallback = onCallNativeAdvertisingCallback;
    try {
      // IOS 传参格式为对象，安卓传参格式为字符串
      if (_type === "IOS") {
        console.log("IOS调用原生激励广告");
        await window.webkit.messageHandlers.callNativeAdvertising.postMessage(
          params
        );
      } else if (_type === "Android") {
        console.log("Android调用原生激励广告");
        await window.hellotalk.callNativeAdvertising(JSON.stringify(params));
      }
    } catch (e) {
      console.log("出错了", e);
      resolve({});
    }
  });
};

/**
 * @description 前往直播聚合页
 * @returns {void}
 */
export const goLiveList = async (source?: string) => {
  // 5570之后跳直播聚合页
  await gotoHTDeepLink(`hellotalk://live_video_list?source=${source}`);
  goBack();
};

/**
 * @description 前往语聊聚合页
 * @param {boolean} isClickGoVoice 是否进行数据上报
 * @returns {void}
 */
export const goVoiceList = async (source?: string) => {
  // 5570之后跳语聊聚合页
  await gotoHTDeepLink(`hellotalk://live_voice_list?source=${source}`);
  goBack();
};
/**
 * @description 调用原生toast
 * @version: 6.1.70
 * @description 调用原生toast
 * @param {string} message 提示信息
 */
export const callNativeToast = (message: string) => {
  const _type = getOsType();
  try {
    // IOS 传参格式为对象，安卓传参格式为字符串
    if (_type === "IOS") {
      window.webkit.messageHandlers.showToast.postMessage(message);
    } else if (_type === "Android") {
      window.hellotalk.showToast(message);
    }
  } catch (e) {
    console.log("callNativeToast error", e);
  }
};

/**
 * @description 根据场景获取商品信息
 * @param params 参数对象，包含 scene 场景参数和 isPromoActive 促销活动状态
 * @param params.scene 场景参数，如 "3"-蓝色特权页、"40"-SKU 半窗、"9"-赠送礼物页面
 * @param params.isPromoActive 是否为促销新玩法，可选参数
 */
export const getProductInfoFromScene = (params: {
  scene: string;
  isPromoActive?: boolean;
}): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (isIOSPhone) {
      const onProductSceneCallback = (res: string) => {
        resolve(res);
      };
      window.getProductInfoFromSceneCallBack = onProductSceneCallback;
      try {
        window.webkit.messageHandlers.getProductInfoFromScene.postMessage(
          params
        );
      } catch (error) {
        reject(error);
        console.log("获取商品信息失败", error);
      }
    } else if (isAndroidPhone) {
      const onProductSceneCallback = (res: string) => {
        resolve(JSON.parse(res));
      };
      window.getProductInfoFromSceneCallBack = onProductSceneCallback;
      try {
        window.hellotalk.getProductInfoFromScene(JSON.stringify(params));
      } catch (error) {
        reject(error);
      }
    } else {
      reject(new Error("非客户端"));
    }
  });
};

/**
 * @description 前往更新 HelloTalk
 * Android: 版本 >= 6.3.30 时调用 openHelloTalkDetail，否则跳转 hellotalk.cn
 * iOS: 跳转 App Store
 * @example import { gotoUpdateHelloTalk } from "@ht/utils";
 * gotoUpdateHelloTalk("6.3.20");
 */
export const gotoUpdateHelloTalk = () => {
  if (isIOSPhone) {
    const APP_STORE_URL = "https://apps.apple.com/app/hellotalk/id557130558";
    try {
      window.webkit.messageHandlers.onDeepLinkCallback.postMessage(
        encodeURI(APP_STORE_URL)
      );
    } catch {
      location.href = APP_STORE_URL;
    }
  } else if (isAndroidPhone) {
    try {
      window.hellotalk.openHelloTalkDetail();
    } catch {
      window.location.href = "https://www.hellotalk.cn/";
    }
  }
};

/**
 * @description 获取指定特权体验卡的剩余次数（用于体验记录「剩余 N 次」，如额外10次翻译）
 * @param {string} params.scene_id 二级分类ID
 * @param {string} params.feature_id 三级分类ID
 * @returns {Promise<number>} 剩余次数，取不到（非客户端/低版本/异常）统一返回 0
 * @version 客户端 6.3.80 起支持
 * iOS：postMessage 传对象，走 getCardExperienceCountCallback 回调（字符串/数字）
 * Android：位置参数分开传（scene_id, feature_id），同步 return 数字/字符串
 */
export const getCardExperienceCount = (params: {
  scene_id: string;
  feature_id: string;
}): Promise<number> => {
  return new Promise(resolve => {
    try {
      if (isIOSPhone) {
        window.getCardExperienceCountCallback = (res: string | number) => {
          resolve(parseInt(String(res), 10) || 0);
        };
        window.webkit.messageHandlers.getCardExperienceCount.postMessage(params);
      } else if (isAndroidPhone) {
        const data = window.hellotalk.getCardExperienceCount(
          params.scene_id,
          params.feature_id
        );
        resolve(parseInt(String(data), 10) || 0);
      } else {
        resolve(0);
      }
    } catch (e) {
      console.log("getCardExperienceCount error", e);
      resolve(0);
    }
  });
};
